import { useSelector } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { GridDensity, GridState } from '@mui/x-data-grid-pro'
import {
  selectPestPacLocationDuplicatesFilters,
  setSearch,
} from '@user/store/duplicates/PestPacLocations/slice'
import { ILocationSearchModel } from '@user/query/useDuplicateLocationSearch'

export const useData = () => {
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const dispatch = useAppDispatch()
  const {
    orderBy,
    page,
    perPage: rowsPerPage,
    resultCount,
    duplicates,
  } = useSelector(selectPestPacLocationDuplicatesFilters)

  const [searchText, setSearchText] = useState<string>('')
  const [tableRows, setTableRows] = useState<ILocationSearchModel[]>(duplicates)
  const [searchResultCount, setSearchResultCount] = useState<number>(0)

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSearchText(e.target.value)
    dispatch(setSearch(e.target.value))
  }

  const densityModeGeneralPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ==
      IUserPreferenceGeneralListItemType.PestPacLocationDensityMode,
  )

  const pageSizeGeneralPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.PestPacLocationPageSize,
  )

  const perPage = Number(pageSizeGeneralPreference?.value || rowsPerPage)
  const stringValue = densityModeGeneralPreference?.value ?? 'standard'
  const densityMode = stringValue as GridDensity

  const onDensityChange = (density: string) => {
    if (densityMode != density) {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.PestPacLocationDensityMode,
          value: density,
        }),
      )
    }
  }

  const onStateChange = (state: GridState) => {
    if (state.density) {
      onDensityChange(state.density.value)
    }
  }

  const processedRows = useMemo(() => {
    const lowerSearchText = searchText.toLowerCase()

    const filteredRows = lowerSearchText.trim()
      ? duplicates.filter((row: ILocationSearchModel) => {
          return (
            row.firstName?.toLowerCase().includes(lowerSearchText) ||
            row.branch?.toLowerCase().includes(lowerSearchText) ||
            row.company?.toLowerCase().includes(lowerSearchText) ||
            row.phone?.toLowerCase().includes(lowerSearchText) ||
            row.email?.toLowerCase().includes(lowerSearchText) ||
            row.address?.address?.toLowerCase().includes(lowerSearchText) ||
            row.address?.city?.toLowerCase().includes(lowerSearchText) ||
            row.address?.state?.toLowerCase().includes(lowerSearchText) ||
            row.address?.postalCode?.toLowerCase().includes(lowerSearchText)
          )
        })
      : duplicates

    const [field, direction] = orderBy.split(' ')
    const order = direction === 'desc' ? -1 : 1

    const sortedRows = [...filteredRows].sort((a, b) => {
      // Handle nested address.address specifically
      let aValue: any = a[field]
      let bValue: any = b[field]
      if (field.toLowerCase() === 'address') {
        aValue = a.address?.address
        bValue = b.address?.address
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * order
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * order
      }
      return 0
    })

    const startIndex = (page - 1) * perPage
    const paginatedRows = sortedRows.slice(startIndex, startIndex + perPage)

    return {
      paginatedRows,
      total: filteredRows.length,
    }
  }, [duplicates, searchText, orderBy, page, perPage])

  useEffect(() => {
    setTableRows(processedRows.paginatedRows)
    setSearchResultCount(processedRows.total)
  }, [dispatch, processedRows])

  return {
    duplicatesCount: resultCount,
    orderBy,
    page,
    perPage,
    densityMode,
    onStateChange,
    handleSearchChange,
    searchText,
    tableRows,
    searchResultCount,
  }
}
